import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Check,
  ChevronsUpDown,
  Pencil,
  Plus,
  RotateCcw,
  SplitSquareHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ApiError } from '../../lib/api';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { TOTAL_BPS, buildEvenAllocations, evenSplitBps } from '../../lib/xeroAllocation';
import { useCurrentUser } from '../auth';
import type {
  XeroAllocationMethod,
  XeroAllocationSuggestion,
  XeroConfirmedRuleRow,
  XeroPendingAllocationRow,
  XeroPickableAccount,
  XeroTenantConnection,
} from '../../types/xero';
import {
  confirmXeroAllocation,
  deleteXeroAllocationRule,
  getXeroAllocations,
  getXeroConnections,
} from './api';

const money = (cents: number, currency: string) => formatReportingAmount(cents / 100, currency);
const pct = (shareBps: number) => `${(shareBps / 100).toFixed(shareBps % 100 === 0 ? 0 : 1)}%`;

// ---- pure client helpers (method / share reconciliation) -------------------

type EditorTarget = { accountId: string; accountName: string; shareBps: number };

const METHOD_LABEL: Record<XeroAllocationMethod, { label: string; variant: BadgeVariant }> = {
  'even-named': { label: 'Even (named)', variant: 'info' },
  'even-all-children': { label: 'Even (all locations)', variant: 'info' },
  custom: { label: 'Custom', variant: 'warning' },
  'group-only': { label: 'Kept on group', variant: 'neutral' },
};

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

// True if the shares are an even split for the set's size (order-independent).
function sharesAreEven(shareBpsList: number[]): boolean {
  const expected = [...evenSplitBps(shareBpsList.length)].sort((x, y) => x - y);
  const actual = [...shareBpsList].sort((x, y) => x - y);
  return expected.length === actual.length && expected.every((v, i) => v === actual[i]);
}

// Derive the rule's method tag from the resolved targets. group-only = 100% on
// the group account; the suggestion method survives only if the operator kept the
// suggested set AND an even split as-is; any other shape is 'custom'.
function deriveMethod(
  targets: EditorTarget[],
  groupAccountId: string | null,
  suggestion?: { ids: string[]; method: 'even-named' | 'even-all-children' },
): XeroAllocationMethod {
  if (
    groupAccountId &&
    targets.length === 1 &&
    targets[0]!.accountId === groupAccountId &&
    targets[0]!.shareBps === TOTAL_BPS
  ) {
    return 'group-only';
  }
  if (
    suggestion &&
    sameSet(
      targets.map((t) => t.accountId),
      suggestion.ids,
    ) &&
    sharesAreEven(targets.map((t) => t.shareBps))
  ) {
    return suggestion.method;
  }
  return 'custom';
}

// Even-share targets over an account set (seed + "reset to even").
function evenTargets(
  ids: string[],
  nameById: Map<string, string>,
): EditorTarget[] {
  return buildEvenAllocations(ids).map((a) => ({
    accountId: a.accountId,
    accountName: nameById.get(a.accountId) ?? a.accountId,
    shareBps: a.shareBps,
  }));
}

// ---- page ------------------------------------------------------------------

// Admin-only grouped-invoice allocation management (Phase 1c). Two sections:
// "Needs review" (pending group lines pushed down to locations by default —
// accept the suggested even split, edit the accounts/shares, or keep on group)
// and "Confirmed rules" (the browsable history — edit or delete a rule). Every
// confirm/edit/delete saves an audited rule + recomputes so the held revenue
// flows to the right accounts.
export function XeroAllocationsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState<string | null>(null);

  const connections = useQuery({
    queryKey: ['xero', 'connections'],
    queryFn: getXeroConnections,
    enabled: me.role === 'admin',
  });
  const orgs: XeroTenantConnection[] = connections.data?.connections ?? [];
  const activeTenant = tenantId ?? orgs[0]?.tenantId ?? null;

  const allocations = useQuery({
    queryKey: ['xero', 'allocations', activeTenant],
    queryFn: () => getXeroAllocations(activeTenant as string),
    enabled: me.role === 'admin' && !!activeTenant,
  });

  const data = allocations.data;
  const accounts = data?.accounts ?? [];
  const nameById = useMemo(() => new Map(accounts.map((a) => [a.id, a.name])), [accounts]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['xero', 'allocations', activeTenant] });
    void qc.invalidateQueries({ queryKey: ['xero', 'revenue'] });
  };

  const confirm = useMutation({
    mutationFn: confirmXeroAllocation,
    onSuccess: () => {
      invalidate();
      toast.success('Saved — the rule will auto-apply to future repeats.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't save the split."),
  });

  const remove = useMutation({
    mutationFn: deleteXeroAllocationRule,
    onSuccess: () => {
      invalidate();
      toast.success('Rule deleted — the line returns to review on the next recompute.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete the rule."),
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can allocate grouped invoices.</p>;
  }

  const pending = data?.pending ?? [];
  const confirmed = data?.confirmed ?? [];
  const busy = confirm.isPending || remove.isPending;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Grouped invoices — allocation"
        description="Split a group invoice across its locations. Group invoices push down to the group's locations by default — accept the split, adjust it, or keep it on the group. Confirm once; the monthly repeat auto-applies."
        backHref="/settings/xero"
      />

      {connections.isLoading ? (
        <CenteredSpinner />
      ) : orgs.length === 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          No Xero organisations connected.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            {orgs.length > 1 && (
              <select
                value={activeTenant ?? ''}
                onChange={(e) => setTenantId(e.target.value)}
                className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
              >
                {orgs.map((o) => (
                  <option key={o.tenantId} value={o.tenantId}>
                    {o.tenantName ?? o.tenantId}
                  </option>
                ))}
              </select>
            )}
          </div>

          {allocations.isLoading ? (
            <CenteredSpinner />
          ) : allocations.error ? (
            <p className="text-sm text-red-600">Couldn't load allocations.</p>
          ) : (
            <>
              {/* --- Needs review --- */}
              <section className="space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="text-base font-semibold text-slate-900">Needs review</h2>
                  {data && (
                    <span className="text-sm text-slate-500">
                      {pending.length} pending ·{' '}
                      {money(data.totalPendingCents, pending[0]?.currency ?? 'AUD')} held
                    </span>
                  )}
                </div>

                {pending.length === 0 ? (
                  <div className="rounded-md border border-slate-200 bg-white p-8 text-center">
                    <SplitSquareHorizontal className="mx-auto h-8 w-8 text-slate-300" />
                    <p className="mt-2 text-sm font-medium text-slate-700">Nothing to review.</p>
                    <p className="mt-0.5 text-sm text-slate-500">
                      Group invoices awaiting an allocation decision show up here after a sync.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pending.map((row) => (
                      <PendingCard
                        key={`${row.contactId}:${row.signature}`}
                        row={row}
                        accounts={accounts}
                        nameById={nameById}
                        busy={busy}
                        onConfirm={(targets, method) =>
                          confirm.mutate({
                            tenantId: activeTenant as string,
                            contactId: row.contactId,
                            signature: row.signature,
                            descriptionSample: row.descriptionSample,
                            method,
                            targets,
                          })
                        }
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* --- Confirmed rules --- */}
              <ConfirmedRules
                rows={confirmed}
                accounts={accounts}
                nameById={nameById}
                busy={busy}
                onEdit={(row, targets, method) =>
                  confirm.mutate({
                    tenantId: activeTenant as string,
                    contactId: row.contactId,
                    signature: row.signature,
                    descriptionSample: row.descriptionSample,
                    method,
                    targets,
                  })
                }
                onDelete={(row) =>
                  remove.mutate({
                    tenantId: activeTenant as string,
                    contactId: row.contactId,
                    signature: row.signature,
                  })
                }
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---- pending review card ---------------------------------------------------

function PendingCard({
  row,
  accounts,
  nameById,
  busy,
  onConfirm,
}: {
  row: XeroPendingAllocationRow;
  accounts: XeroPickableAccount[];
  nameById: Map<string, string>;
  busy: boolean;
  onConfirm: (targets: { accountId: string; shareBps: number }[], method: XeroAllocationMethod) => void;
}) {
  const suggestion = {
    ids: row.suggested.map((s) => s.accountId),
    method: row.suggestionMethod,
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-900">{row.groupAccountName}</span>
          <Badge variant={row.suggestionMethod === 'even-all-children' ? 'warning' : 'info'}>
            {row.suggestionMethod === 'even-all-children'
              ? 'Pushed down to all locations'
              : 'Named split'}
          </Badge>
        </div>
        <div className="truncate text-sm text-slate-500" title={row.descriptionSample}>
          {row.descriptionSample || '(no description)'}
        </div>
        <div className="mt-0.5 text-xs text-slate-400">
          {row.invoiceCount} invoice{row.invoiceCount === 1 ? '' : 's'} ·{' '}
          {money(row.totalPendingCents, row.currency)} held
        </div>
      </div>

      <AllocationEditor
        accounts={accounts}
        nameById={nameById}
        groupAccountId={row.groupAccountId}
        groupAccountName={row.groupAccountName}
        initialTargets={row.suggested}
        suggestion={suggestion}
        busy={busy}
        confirmLabel="Confirm split"
        onConfirm={onConfirm}
      />
    </div>
  );
}

// ---- confirmed rules (history / management) --------------------------------

function ConfirmedRules({
  rows,
  accounts,
  nameById,
  busy,
  onEdit,
  onDelete,
}: {
  rows: XeroConfirmedRuleRow[];
  accounts: XeroPickableAccount[];
  nameById: Map<string, string>;
  busy: boolean;
  onEdit: (
    row: XeroConfirmedRuleRow,
    targets: { accountId: string; shareBps: number }[],
    method: XeroAllocationMethod,
  ) => void;
  onDelete: (row: XeroConfirmedRuleRow) => void;
}) {
  const [editing, setEditing] = useState<XeroConfirmedRuleRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<XeroConfirmedRuleRow | null>(null);

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">Confirmed rules</h2>
        <span className="text-sm text-slate-500">{rows.length} rule{rows.length === 1 ? '' : 's'}</span>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No confirmed allocation rules yet. Confirming a split above creates one here.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Contact / description</th>
                <th className="px-4 py-2 font-medium">Split</th>
                <th className="px-4 py-2 font-medium">Method</th>
                <th className="px-4 py-2 font-medium">Confirmed by</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => {
                const m = METHOD_LABEL[r.method];
                return (
                  <tr key={`${r.contactId}:${r.signature}`} className="align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {r.contactName ?? r.contactId}
                      </div>
                      <div className="max-w-md truncate text-xs text-slate-500" title={r.descriptionSample}>
                        {r.descriptionSample || '(no description)'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.targets.map((t) => (
                          <span
                            key={t.accountId}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600"
                          >
                            {t.accountName}
                            <span className="text-slate-400">{pct(t.shareBps)}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={m.variant}>{m.label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-700">{r.updatedBy}</div>
                      <div className="text-xs text-slate-400">
                        {new Date(r.updatedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => setEditing(r)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          onClick={() => setConfirmDelete(r)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>Edit allocation rule</DialogTitle>
                <DialogDescription className="truncate" title={editing.descriptionSample}>
                  {editing.descriptionSample || '(no description)'}
                </DialogDescription>
              </DialogHeader>
              <AllocationEditor
                accounts={accounts}
                nameById={nameById}
                groupAccountId={editing.groupAccountId}
                groupAccountName={editing.groupAccountName}
                initialTargets={editing.targets}
                busy={busy}
                confirmLabel="Save rule"
                onConfirm={(targets, method) => {
                  onEdit(editing, targets, method);
                  setEditing(null);
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          {confirmDelete && (
            <>
              <DialogHeader>
                <DialogTitle>Delete this rule?</DialogTitle>
                <DialogDescription>
                  The matching group invoice line returns to review on the next recompute, and its
                  revenue is held again until re-allocated.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmDelete(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={busy}
                  onClick={() => {
                    onDelete(confirmDelete);
                    setConfirmDelete(null);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete rule
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ---- the shared split editor -----------------------------------------------

function AllocationEditor({
  accounts,
  nameById,
  groupAccountId,
  groupAccountName,
  initialTargets,
  suggestion,
  busy,
  confirmLabel,
  onConfirm,
}: {
  accounts: XeroPickableAccount[];
  nameById: Map<string, string>;
  groupAccountId: string | null;
  groupAccountName: string | null;
  initialTargets: XeroAllocationSuggestion[];
  suggestion?: { ids: string[]; method: 'even-named' | 'even-all-children' };
  busy: boolean;
  confirmLabel: string;
  onConfirm: (targets: { accountId: string; shareBps: number }[], method: XeroAllocationMethod) => void;
}) {
  const [targets, setTargets] = useState<EditorTarget[]>(
    initialTargets.map((t) => ({
      accountId: t.accountId,
      accountName: t.accountName,
      shareBps: t.shareBps,
    })),
  );
  const selectedIds = useMemo(() => new Set(targets.map((t) => t.accountId)), [targets]);
  const sum = targets.reduce((s, t) => s + t.shareBps, 0);
  const balanced = sum === TOTAL_BPS;

  const resetEven = () =>
    setTargets((prev) => evenTargets(prev.map((t) => t.accountId), nameById));

  const addAccount = (a: XeroPickableAccount) =>
    setTargets((prev) => evenTargets([...prev.map((t) => t.accountId), a.id], nameById));

  const removeAccount = (id: string) =>
    setTargets((prev) => evenTargets(prev.filter((t) => t.accountId !== id).map((t) => t.accountId), nameById));

  const setShare = (id: string, pctValue: number) =>
    setTargets((prev) =>
      prev.map((t) =>
        t.accountId === id ? { ...t, shareBps: Math.max(0, Math.round(pctValue * 100)) } : t,
      ),
    );

  const keepOnGroup = () => {
    if (!groupAccountId) return;
    onConfirm([{ accountId: groupAccountId, shareBps: TOTAL_BPS }], 'group-only');
  };

  const confirmSplit = () => {
    if (!balanced || targets.length === 0) return;
    const method = deriveMethod(targets, groupAccountId, suggestion);
    onConfirm(
      targets.map((t) => ({ accountId: t.accountId, shareBps: t.shareBps })),
      method,
    );
  };

  return (
    <div className="mt-3 space-y-3">
      {targets.length === 0 && (
        <p className="text-xs text-amber-700">
          No target accounts — add the accounts this line covers, or keep it on the group.
        </p>
      )}

      <div className="space-y-1.5">
        {targets.map((t) => (
          <div key={t.accountId} className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{t.accountName}</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={Number((t.shareBps / 100).toFixed(2))}
                onChange={(e) => setShare(t.accountId, Number(e.target.value))}
                className="h-8 w-20 rounded-md border border-slate-300 bg-white px-2 text-right text-sm"
              />
              <span className="text-xs text-slate-400">%</span>
            </div>
            <button
              type="button"
              onClick={() => removeAccount(t.accountId)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Remove"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <AddAccountPicker
          accounts={accounts}
          excludeIds={selectedIds}
          onPick={addAccount}
        />
        <Button variant="ghost" size="sm" onClick={resetEven} disabled={targets.length === 0}>
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to even
        </Button>
        <span
          className={[
            'ml-auto text-xs',
            balanced ? 'text-slate-400' : 'text-rose-600',
          ].join(' ')}
        >
          {pct(sum)} allocated{balanced ? '' : ' — must total 100%'}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <Button size="sm" disabled={busy || !balanced || targets.length === 0} onClick={confirmSplit}>
          <Check className="h-3.5 w-3.5" />
          {confirmLabel}
        </Button>
        {groupAccountId && (
          <Button variant="outline" size="sm" disabled={busy} onClick={keepOnGroup}>
            <Building2 className="h-3.5 w-3.5" />
            Keep on group
            {groupAccountName ? <span className="text-slate-400">· {groupAccountName}</span> : null}
          </Button>
        )}
      </div>
    </div>
  );
}

function AddAccountPicker({
  accounts,
  excludeIds,
  onPick,
}: {
  accounts: XeroPickableAccount[];
  excludeIds: Set<string>;
  onPick: (account: XeroPickableAccount) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = accounts.filter((a) => !excludeIds.has(a.id));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-3.5 w-3.5" />
          Add account
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts…" />
          <CommandList>
            <CommandEmpty>No accounts.</CommandEmpty>
            <CommandGroup>
              {options.map((a) => (
                <CommandItem
                  key={a.id}
                  value={a.name}
                  onSelect={() => {
                    onPick(a);
                    setOpen(false);
                  }}
                >
                  {a.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
